import { Button, Divider, Select } from 'react-dsgov';
import { SelectOptions } from '@govbr-ds/react-components/dist/components/BrSelect';
import { useState, useMemo, useCallback, useEffect } from 'react';

interface IProps {
  pageCount?: number;
  itemCount?: number;
  initialPage?: number;
  selectedPage?: number;
  selectedPageSize?: number;
  pageSizeOptions?: number[];
  defaultPageSize?: number;
  showPageCombo?: boolean;
  onChangePage?: (pageNumber: number) => void;
  onChangeSizePage?: (pageSize: number) => void;
}

function Paginacao({
  pageCount,
  itemCount,
  initialPage,
  selectedPage,
  selectedPageSize,
  pageSizeOptions,
  defaultPageSize,
  showPageCombo,
  onChangePage,
  onChangeSizePage,
}: IProps) {
  const [currentPage, setCurrentPage] = useState(initialPage || 1);
  const [currentPagination, setCurrentPagination] = useState(defaultPageSize || 10);

  const currentPageCount = useMemo(() => {
    return itemCount ? Math.ceil(itemCount / currentPagination) : pageCount;
  }, [itemCount, currentPagination]);

  const getPaginationOptions = useCallback((): SelectOptions[] => {
    if (pageSizeOptions) {
      const options = [];
      for (const option of pageSizeOptions) {
        options.push({ label: String(option), value: String(option) });
      }
      return options;
    } else {
      return [
        { label: '10', value: '10' },
        { label: '20', value: '20' },
        { label: '50', value: '50' },
        { label: '100', value: '100' },
      ];
    }
  }, [pageSizeOptions]);

  useEffect(() => {
    setCurrentPage(selectedPage ?? currentPage);
  }, [selectedPage]);

  useEffect(() => {
    setCurrentPagination(selectedPageSize ?? currentPagination);
  }, [selectedPageSize]);

  const handleClickPrevPage = useCallback(() => {
    setCurrentPage((oldPage) => {
      if (oldPage === 1) {
        return oldPage;
      } else {
        onChangePage?.(oldPage - 1);
        return oldPage - 1;
      }
    });
  }, []);

  const handleClickNextPage = useCallback(() => {
    setCurrentPage((oldPage) => {
      if (currentPageCount && oldPage + 1 > currentPageCount) {
        return oldPage;
      } else {
        onChangePage?.(oldPage + 1);
        return oldPage + 1;
      }
    });
  }, [currentPageCount, setCurrentPage]);

  const handleChangePageSize = useCallback(
    (value: string) => {
      setCurrentPagination(Number(value));
      onChangeSizePage?.(Number(value));

      // Verifica se a página atual é maior do que o novo page count
      if (itemCount) {
        const pageCountCalc = Math.ceil(itemCount / Number(value));

        setCurrentPage((oldPage) => {
          if (oldPage > pageCountCalc) {
            onChangePage?.(pageCountCalc);
            return pageCountCalc;
          } else {
            return oldPage;
          }
        });
      }
    },
    [onChangePage, setCurrentPage, setCurrentPagination, itemCount, currentPagination]
  );

  const handleChangePage = useCallback(
    (value: string) => {
      setCurrentPage(Number(value));
      onChangePage?.(Number(value));
    },
    [onChangePage]
  );

  const getPageOptions = useCallback((): SelectOptions[] => {
    const options = [];
    if (itemCount) {
      const pageCountCalc = Math.ceil(itemCount / currentPagination);

      for (let i = 0; i < pageCountCalc; i++) {
        options.push({ label: String(i + 1), value: String(i + 1) });
      }
    }

    return options;
  }, [itemCount, currentPagination]);

  return (
    <nav className='br-pagination'>
      <div className='pagination-per-page'>
        <Select
          options={getPaginationOptions()}
          value={String(currentPagination)}
          onChange={handleChangePageSize}
          label='Exibir'
        />
      </div>
      <Divider mx={3} />
      <div className='pagination-go-to-page d-none d-sm-flex'>
        <span className='current'>{(currentPage - 1) * currentPagination + 1}</span>–
        <span className='per-page'>
          {itemCount && currentPage * currentPagination > itemCount ? itemCount : currentPage * currentPagination}
        </span>
        &nbsp;de&nbsp;<span className='total'>{itemCount}</span>&nbsp;itens
      </div>
      <div className='pagination-go-to-page d-none d-sm-flex ml-auto'>
        {showPageCombo && (
          <Select options={getPageOptions()} label='Página' value={String(currentPage)} onChange={handleChangePage} />
        )}
      </div>
      <Divider mx={3} />
      <div className='pagination-arrows ml-auto ml-sm-0'>
        <Button
          disabled={itemCount === 0 || currentPage === 1}
          onClick={handleClickPrevPage}
          circle
          icon='fas fa-angle-left'
          aria-label='Página anterior'
        />
        <Button
          disabled={itemCount === 0 || currentPage === currentPageCount}
          onClick={handleClickNextPage}
          circle
          icon='fas fa-angle-right'
          aria-label='Próxima página'
        />
      </div>
    </nav>
  );
}

export default Paginacao;
